import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';
import { UserResponse, AppointmentResponse, TimeslotInfo, BookAppointmentRequest } from '../types/api';
import { useConfirmation } from '../hooks/useConfirmation';
import { useTranslation } from '../context/LanguageContext';
import { toGreekUpperCase } from '../utils/greekUtils';
import ConfirmationDialog from './ConfirmationDialog';
import AdminCancelAppointmentDialog from './AdminCancelAppointmentDialog';
import './Modal.css';

interface EditClientAppointmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientUpdated: () => void;
  client: UserResponse | null;
}

const EditClientAppointmentsModal: React.FC<EditClientAppointmentsModalProps> = ({ 
  isOpen, 
  onClose, 
  onClientUpdated, 
  client 
}) => {
  const { t, formatDate: formatDateWithLocale } = useTranslation();
  const [appointments, setAppointments] = useState<AppointmentResponse[]>([]);
  const [timeslots, setTimeslots] = useState<TimeslotInfo[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [cancelDialogAppointmentId, setCancelDialogAppointmentId] = useState<number | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const { confirmation, showConfirmation, hideConfirmation } = useConfirmation();

  const sortedAppointments = useMemo(() =>
    [...appointments].sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime()),
    [appointments]
  );

  const fetchClientAppointments = useCallback(async () => {
    if (!client) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Get appointments from a very early start date to capture all history
      const startDate = '2020-01-01'; // Start from a date that predates the system
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1); // Get future appointments too
      const endDateStr = endDate.toISOString().split('T')[0];
      
      const appointmentsResponse = await apiService.getClientAppointments(client.id, startDate, endDateStr);
      if (appointmentsResponse.success && appointmentsResponse.data) {
        setAppointments(appointmentsResponse.data);
      }
    } catch (err: any) {
      console.error('Error fetching client appointments:', err);
      setError(t('failedToLoadClientAppointments'));
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  const fetchAvailableTimeslots = useCallback(async () => {
    setIsLoadingTimeslots(true);
    setError('');
    
    try {
      const response = await apiService.getTimeslots(selectedDate);
      if (response.success && response.data) {
        // Show all timeslots for admin (no availability restrictions)
        let availableSlots = response.data.timeslots;

        // If the selected date is today, filter out past timeslots
        const today = new Date().toISOString().split('T')[0];
        if (selectedDate === today) {
          const now = new Date();
          const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes
          
          availableSlots = availableSlots.filter(slot => {
            const [hours, minutes] = slot.startTime.split(':').map(Number);
            const slotTime = hours * 60 + minutes;
            return slotTime > currentTime; // Only show future timeslots
          });
        }

        setTimeslots(availableSlots);
      }
    } catch (err: any) {
      console.error('Error fetching timeslots:', err);
      setError(t('failedToLoadAvailableTimeslots'));
    } finally {
      setIsLoadingTimeslots(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (isOpen && client) {
      // Fetch appointments
      fetchClientAppointments();
      
      // Clear previous messages
      setError('');
      setSuccess('');
    }
  }, [isOpen, client, fetchClientAppointments]);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableTimeslots();
    }
  }, [isOpen, selectedDate, fetchAvailableTimeslots]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const handlePrevDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleBookAppointment = async (timeslot: TimeslotInfo) => {
    if (!client) return;
    
    showConfirmation({
      title: t('bookAppointment'),
      message: `${t('confirmBookAppointmentMessage')} ${client.name} ${t('at')} ${formatTime(timeslot.startTime)} - ${formatTime(timeslot.endTime)}?`,
      confirmText: t('bookAppointment'),
      type: 'info',
      onConfirm: () => {
        hideConfirmation();
        confirmBookAppointment(timeslot);
      }
    });
  };

  const confirmBookAppointment = async (timeslot: TimeslotInfo) => {
    if (!client) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const appointmentData: BookAppointmentRequest = {
        appointmentDate: selectedDate,
        startTime: timeslot.startTime,
        endTime: timeslot.endTime,
        userId: client.id,
        isAdminOverride: true
      };
      
      const response = await apiService.bookAppointmentAsAdmin(appointmentData);
      if (response.success) {
        setSuccess(t('appointmentBookedSuccessfully'));
        fetchClientAppointments(); // Refresh appointments
        fetchAvailableTimeslots(); // Refresh timeslots
        
        // Trigger global event to refresh dashboard
        window.dispatchEvent(new CustomEvent('appointmentBooked'));
      } else {
        setError(response.message || t('failedToBookAppointment'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorBookingAppointment'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAppointment = (appointmentId: number) => {
    setCancelDialogAppointmentId(appointmentId);
  };

  const confirmCancelAppointment = async (countTowardLimit: boolean) => {
    if (cancelDialogAppointmentId === null) return;
    const appointmentId = cancelDialogAppointmentId;

    setIsCancelling(true);
    try {
      const response = await apiService.cancelAppointmentAsAdmin(appointmentId, countTowardLimit);
      if (response.success) {
        setSuccess(t('appointmentCancelledSuccessfully'));
        fetchClientAppointments(); // Refresh appointments
        fetchAvailableTimeslots(); // Refresh timeslots in case it affects availability

        // Trigger global event to refresh dashboard
        window.dispatchEvent(new CustomEvent('appointmentCancelled'));
      } else {
        setError(response.message || t('failedToCancelAppointment'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorCancellingAppointment'));
    } finally {
      setIsCancelling(false);
      setCancelDialogAppointmentId(null);
    }
  };

  const handlePermanentDeleteAppointment = async (appointmentId: number) => {
    showConfirmation({
      title: t('deleteAppointment'),
      message: t('confirmPermanentDeleteMessage'),
      confirmText: t('deleteAppointment'),
      type: 'danger',
      onConfirm: () => {
        hideConfirmation();
        confirmPermanentDeleteAppointment(appointmentId);
      }
    });
  };

  const confirmPermanentDeleteAppointment = async (appointmentId: number) => {
    try {
      const response = await apiService.permanentDeleteAppointment(appointmentId);
      if (response.success) {
        setSuccess(t('appointmentDeletedSuccessfully'));
        fetchClientAppointments(); // Refresh appointments
        fetchAvailableTimeslots(); // Refresh timeslots in case it affects availability

        // Trigger global event to refresh dashboard
        window.dispatchEvent(new CustomEvent('appointmentDeleted'));
      } else {
        setError(response.message || t('failedToDeleteAppointment'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorDeletingAppointment'));
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(d);
  };

  const formatDateLong = (dateString: string) => {
    return formatDateWithLocale(dateString, { weekday: true });
  };

  const formatTime = (timeString: string) => {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    return timeString;
  };

  const formatCancelledTime = (datetimeString: string) => {
    const date = new Date(datetimeString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatCancelledDate = (datetimeString: string) => {
    const date = new Date(datetimeString);
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const getAvailabilityText = (timeslot: TimeslotInfo) => {
    const available = timeslot.capacity - timeslot.currentBookings;
    if (available === timeslot.capacity) {
      return `${timeslot.capacity} ${t('spotsAvailable')}`;
    }
    return `${available} ${t('of')} ${timeslot.capacity} ${t('spotsAvailable')}`;
  };

  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30); // Allow booking up to 30 days in advance
    return maxDate.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('manageAppointments')} - {client?.name}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="modal-body">
          <div className="appointments-modal">
          {/* Book New Appointment */}
          <div className="section">
            <h3>{t('bookNewAppointment')}</h3>
            
            <div className="date-selection">
              <label htmlFor="appointmentDate">{t('selectDate')}:</label>
              <div className="date-navigation">
                <button
                  className="date-nav-btn"
                  onClick={handlePrevDay}
                  disabled={isLoading || selectedDate <= getMinDate()}
                  title={t('previousDay')}
                >
                  ←
                </button>
                <input
                  type="date"
                  id="appointmentDate"
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={getMinDate()}
                  max={getMaxDate()}
                />
                <button
                  className="date-nav-btn"
                  onClick={handleNextDay}
                  disabled={isLoading || selectedDate >= getMaxDate()}
                  title={t('nextDay')}
                >
                  →
                </button>
              </div>
              <div className="selected-date-display">
                {formatDateLong(selectedDate)}
              </div>
            </div>

            {isLoadingTimeslots ? (
              <div className="loading">{t('loadingAvailableTimeSlots')}</div>
            ) : (
              <div className="timeslots-section">
                {timeslots.length === 0 ? (
                  <div className="no-timeslots">
                    <div className="no-timeslots-icon">📅</div>
                    <h4>{t('noAvailableTimeSlots')}</h4>
                    <p>{t('noAvailableTimeSlotsMessage')}</p>
                  </div>
                ) : (
                  <>
                    <h4>{t('availableTimeSlots')}</h4>
                    <div className="timeslots-grid">
                      {timeslots.map((timeslot) => (
                        <div key={timeslot.id} className="timeslot-card">
                          <div className="timeslot-time">
                            {formatTime(timeslot.startTime)} - {formatTime(timeslot.endTime)}
                          </div>
                          
                          <div className="timeslot-availability">
                            <span className="availability-text">
                              {getAvailabilityText(timeslot)}
                            </span>
                            <div className="availability-bar">
                              <div 
                                className="availability-fill"
                                style={{ 
                                  width: `${((timeslot.capacity - timeslot.currentBookings) / timeslot.capacity) * 100}%` 
                                }}
                              />
                            </div>
                          </div>

                          <button
                            className="book-button"
                            onClick={() => handleBookAppointment(timeslot)}
                            disabled={isLoading}
                          >
                            {isLoading ? t('booking') : t('bookThisSlot')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Appointment History */}
          <div className="section">
            <h3>{t('appointmentHistory')}</h3>
            {appointments.length === 0 ? (
              <p className="no-appointments">{t('noAppointmentsFound')}</p>
            ) : (
              <div className="appointments-history">
                {sortedAppointments
                  .map((appointment) => (
                  <div key={appointment.id} className="appointment-history-item">
                    <div className="appointment-main-info">
                      <div className="appointment-date-time">
                        <span className="appointment-date">{formatDate(appointment.appointmentDate)}</span>
                        <span className="appointment-time">{formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}</span>
                      </div>
                      <div className="appointment-status-actions">
                        <span className={`status-badge ${
                          appointment.status === 1 ? 'active' : 
                          appointment.status === 2 ? 'cancelled' : 'completed'
                        }`}>
                          {appointment.status === 1 ? toGreekUpperCase(t('confirmed')) : 
                           appointment.status === 2 ? toGreekUpperCase(t('cancelled')) : toGreekUpperCase(t('completed'))}
                        </span>
                        {(appointment.status === 1 || appointment.status === 3) && (
                          <button
                            className="btn-cancel-small"
                            onClick={() => handleCancelAppointment(appointment.id)}
                            disabled={isLoading}
                            title={t('cancelAppointment')}
                          >
                            {t('cancel')}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="appointment-details">
                      <div className="booked-info-container">
                        <span className="booked-info">
                          {t('booked')}: {formatDate(appointment.bookedAt)} {t('by')} {appointment.bookedByName}
                        </span>
                        <button
                          className="btn-delete-small"
                          onClick={() => handlePermanentDeleteAppointment(appointment.id)}
                          disabled={isLoading}
                          title={t('deleteAppointment')}
                        >
                          🗑️
                        </button>
                      </div>
                      {appointment.status === 2 && appointment.cancelledAt && (
                        <div className="cancelled-info">
                          {t('cancelledAt')}: {formatCancelledDate(appointment.cancelledAt)} {t('at')} {formatCancelledTime(appointment.cancelledAt)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              {t('close')}
            </button>
          </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        onConfirm={confirmation.onConfirm}
        onCancel={confirmation.onCancel}
      />

      <AdminCancelAppointmentDialog
        isOpen={cancelDialogAppointmentId !== null}
        isLoading={isCancelling}
        onConfirm={confirmCancelAppointment}
        onCancel={() => setCancelDialogAppointmentId(null)}
      />
    </div>
  );
};

export default EditClientAppointmentsModal;